import { FC, useMemo, useState } from "react"
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import { Add, DeleteOutlined } from "@mui/icons-material"
import { SnackbarAlert } from "@/core/components"
import { GuestBot } from "../../interfaces"
import {
  useTemplatesListQuery,
  useSetResponsesMutation,
  useDeleteResponseMutation,
} from "../../store"
import { BindingRow, bindingsToRows, rowsToBindings } from "./bindingsMapper"

interface IProps {
  bot: GuestBot.Bot
}

export const ResponsesEditor: FC<IProps> = ({ bot }) => {
  const { data: templatesRes } = useTemplatesListQuery({
    queryParams: { limit: 200, offset: 0 },
  })
  const templates = templatesRes?.items ?? []
  const [setResponses, { isLoading: isSaving }] = useSetResponsesMutation()
  const [deleteResponse] = useDeleteResponseMutation()

  const [rows, setRows] = useState<BindingRow[]>(
    bindingsToRows(bot.responses ?? []),
  )
  const [snackbar, setSnackbar] = useState<{
    variant: "success" | "error"
    message: string
  } | null>(null)

  const hasFallback = useMemo(() => rows.some((r) => r.isFallback), [rows])
  const canSave = useMemo(
    () => rows.length > 0 && rows.every((r) => !!r.templateId),
    [rows],
  )

  const updateRow = (index: number, patch: Partial<BindingRow>) => {
    setRows((prev) =>
      prev.map((r, i) => (i === index ? { ...r, ...patch } : r)),
    )
  }

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      { triggerText: "", templateId: "", isFallback: false },
    ])
  }

  const removeRow = async (index: number) => {
    const row = rows[index]
    if (row.id) {
      try {
        await deleteResponse({
          pathParams: { id: bot.id, responseId: row.id },
        }).unwrap()
      } catch {
        setSnackbar({
          variant: "error",
          message: "Не удалось удалить привязку",
        })
        return
      }
    }
    setRows((prev) => prev.filter((_, i) => i !== index))
  }

  const save = async () => {
    try {
      await setResponses({
        pathParams: { id: bot.id },
        body: { bindings: rowsToBindings(rows) },
      }).unwrap()
      setSnackbar({ variant: "success", message: "Привязки сохранены" })
    } catch {
      setSnackbar({
        variant: "error",
        message: "Не удалось сохранить привязки",
      })
    }
  }

  return (
    <Stack spacing={2}>
      <Typography variant="h6">Привязки ответов</Typography>
      {rows.length === 0 && (
        <Typography color="text.secondary">Привязок пока нет.</Typography>
      )}
      {rows.map((row, index) => (
        <Stack
          key={row.id ?? `new-${index}`}
          direction={{ xs: "column", md: "row" }}
          spacing={1}
          alignItems={{ md: "center" }}
        >
          <TextField
            label="Триггер (текст)"
            value={row.triggerText}
            disabled={row.isFallback}
            onChange={(e) => updateRow(index, { triggerText: e.target.value })}
            sx={{ flex: 2 }}
          />
          <TextField
            select
            label="Шаблон"
            value={row.templateId}
            onChange={(e) => updateRow(index, { templateId: e.target.value })}
            sx={{ flex: 2 }}
          >
            {templates.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.name}
              </MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            control={
              <Checkbox
                checked={row.isFallback}
                disabled={hasFallback && !row.isFallback}
                onChange={(e) =>
                  updateRow(index, {
                    isFallback: e.target.checked,
                    triggerText: e.target.checked ? "" : row.triggerText,
                  })
                }
              />
            }
            label="Ответ по умолчанию (fallback)"
          />
          <Tooltip title="Удалить эту привязку" arrow>
            <IconButton
              aria-label="Удалить"
              color="error"
              onClick={() => removeRow(index)}
            >
              <DeleteOutlined />
            </IconButton>
          </Tooltip>
        </Stack>
      ))}
      <Box display="flex" gap={1}>
        <Tooltip title="Добавить новую строку привязки" arrow>
          <Button startIcon={<Add />} onClick={addRow}>
            Добавить строку
          </Button>
        </Tooltip>
        <Tooltip title="Сохранить все привязки бота" arrow>
          <span>
            <Button
              variant="contained"
              onClick={save}
              disabled={!canSave || isSaving}
            >
              Сохранить
            </Button>
          </span>
        </Tooltip>
      </Box>
      <SnackbarAlert
        isOpen={!!snackbar}
        onClose={() => setSnackbar(null)}
        variant={snackbar?.variant}
        message={snackbar?.message ?? ""}
      />
    </Stack>
  )
}
